import { useSelector } from 'react-redux'

import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { RootState, useAppDispatch } from 'store'

export type GcrmNotificationState = {
  version: string
  start: number
  end: number
  countryCodeList: string[]
  notice: {
    title: string
    description: string
    schedule: string
    unavailableServices: string
  }
}

type ExperimentState = {
  countryChangeFeatureFlag: boolean | null
  iam3RedirectionCountries: string[] | null
  gcrmNotificationStates: GcrmNotificationState[] | null
  deviceIdentificationSupportedCountries: string[] | null
}

const initialState: ExperimentState = {
  countryChangeFeatureFlag: null,
  iam3RedirectionCountries: null,
  gcrmNotificationStates: null,
  deviceIdentificationSupportedCountries: null
}

export const experimentsSlice = createSlice({
  name: 'experiments',
  initialState,
  reducers: {
    setFlag: (
      state,
      action: PayloadAction<{ flagName: keyof Omit<ExperimentState, 'gcrmNotificationStates'>; value: boolean | string[] | null }>
    ) => {
      const { flagName, value } = action.payload
      state[flagName] = value as (boolean & string[]) | null
    },
    setGcrmDialogStates: (state, action: PayloadAction<GcrmNotificationState[]>) => {
      state.gcrmNotificationStates = action.payload
    }
  }
})

export const { setFlag, setGcrmDialogStates } = experimentsSlice.actions

const fetchFlag = async (groupName: string): Promise<boolean> => {
  try {
    const response = await axios.get<string[]>(process.env.REACT_APP_HOST + '/api/v1/experiments')
    if (Array.isArray(response)) {
      return response.includes(groupName)
    }
    return false
  } catch (error) {
    return false
  }
}

const isWithinTimeRange = (start: number, end: number): boolean => {
  const now = Date.now()
  return now >= start && now < end
}

const getVersionList = (planResponse: any) => {
  return planResponse.versionList[0]
}

const filterApplicableGroups = (version: any, groupName: string) => {
  const applicableGroups = new Map()
  Object.entries(version.experimentGroups).forEach(([key, value]) => {
    applicableGroups.set(key, value)
  })
  return applicableGroups.get(groupName)
}

export const useExperiments = () => {
  const dispatch = useAppDispatch()
  const experiments = useSelector((state: RootState) => state.experiments)

  const setExperimentFlag = (flagName: keyof Omit<ExperimentState, 'gcrmNotificationStates'>, value: boolean) => {
    dispatch(setFlag({ flagName, value }))
  }

  const fetchAndUpdate = async (): Promise<{
    deviceIdentificationSupportedCountries: any
  }> => {
    try {
      let plan = await axios.get(`${process.env.REACT_APP_PLAN_URL}/${process.env.REACT_APP_CLIENT_ID}.json`)
      const version = getVersionList(plan.data)

      //Device Identification
      const experimentGroup = filterApplicableGroups(version, 'deviceIdentification')
      const user = experimentGroup?.conditions.user
      const deviceIdentificationSupportedCountries = user?.countryCodeList
      setExperimentFlag(
        'deviceIdentificationSupportedCountries',
        deviceIdentificationSupportedCountries === undefined ? [] : deviceIdentificationSupportedCountries
      )

      return {
        deviceIdentificationSupportedCountries
      }
    } catch (error) {
      return {
        deviceIdentificationSupportedCountries: null
      }
    }
  }

  const getDeviceIdentificationSupportedFlag = async (countryCode: string): Promise<boolean> => {
    if (process.env.REACT_APP_CHINA_REGION === 'true') {
      return false
    }
    let deviceIdentificationSupportedCountries = experiments.deviceIdentificationSupportedCountries
    if (deviceIdentificationSupportedCountries === null) {
      const fetched = await fetchAndUpdate()
      const fetchedDeviceIdentificationSupportedCountries =
        fetched.deviceIdentificationSupportedCountries !== undefined ? fetched.deviceIdentificationSupportedCountries : []

      return (
        fetchedDeviceIdentificationSupportedCountries.includes(countryCode.toUpperCase()) ||
        fetchedDeviceIdentificationSupportedCountries.includes('all')
      )
    } else {
      return (
        deviceIdentificationSupportedCountries.includes(countryCode.toUpperCase()) ||
        deviceIdentificationSupportedCountries.includes('all')
      )
    }
  }

  const getCountryChangeFlag = async (): Promise<boolean> => {
    const flag = experiments.countryChangeFeatureFlag
    if (flag === null) {
      const value = await fetchFlag('countryChangeFeatureGroup')
      setExperimentFlag('countryChangeFeatureFlag', value)
      return value
    }
    return flag
  }

  const getIam3RedirectionFlag = async (countryCode: string): Promise<boolean> => {
    const iam3RedirectionCountries = experiments.iam3RedirectionCountries
    if (iam3RedirectionCountries === null) {
      let planResponse
      try {
        planResponse = await axios.get(`${process.env.REACT_APP_PLAN_URL}/${process.env.REACT_APP_CLIENT_ID}.json`)
      } catch (error) {
        return false
      }

      const version = getVersionList(planResponse.data)
      const experimentGroup = filterApplicableGroups(version, 'iam3Group')
      const user = experimentGroup?.conditions.user
      const value = user?.countryCodeList
      setExperimentFlag('iam3RedirectionCountries', value)
      return value?.includes(countryCode.toUpperCase()) || value?.includes('all')
    }
    return iam3RedirectionCountries?.includes(countryCode.toUpperCase()) || iam3RedirectionCountries?.includes('all')
  }

  const fetchGcrmNotificationStates = async (): Promise<GcrmNotificationState[]> => {
    let planResponse
    try {
      planResponse = await axios.get(`${process.env.REACT_APP_PLAN_URL}/${process.env.REACT_APP_CLIENT_ID}.json`)
    } catch (error) {
      return [] as GcrmNotificationState[]
    }

    const version = getVersionList(planResponse.data)
    const fetched =
      filterApplicableGroups(version, 'gcrmNotificationStates')?.conditions?.notifications ?? ([] as GcrmNotificationState[])
    setGcrmDialogStates(fetched)

    return fetched
  }

  const getGcrmNotificationStates = async (countryCode: string): Promise<GcrmNotificationState[]> => {
    let gcrmNotificationStates: GcrmNotificationState[] | null = experiments.gcrmNotificationStates

    if (gcrmNotificationStates === null) {
      gcrmNotificationStates = await fetchGcrmNotificationStates()
    }

    if (gcrmNotificationStates.length === 0) {
      return [] as GcrmNotificationState[]
    }

    const filteredStates = gcrmNotificationStates?.filter((state) => {
      return isWithinTimeRange(state.start, state.end) && state.countryCodeList.includes(countryCode)
    })

    return filteredStates ?? ([] as GcrmNotificationState[])
  }

  return { getCountryChangeFlag, getIam3RedirectionFlag, getGcrmNotificationStates, getDeviceIdentificationSupportedFlag }
}
